import { Container } from '@material-ui/core';
import { PedigreeType } from 'api/query/animal';
import animalApi from 'api/query/animal/animal-api';
import { Pedigree } from 'api/query/animal/pedigree';
import LoadContainer from 'components/load-container';
import ScrollShadow from 'components/scroll-shadow';
import React, { useEffect, useRef, useState } from 'react';
import Tree from 'react-d3-tree';
import { PedigreeChartNodeLabel } from './pedigree-chart-node-label';
import { PedigreeChartProps } from './pedigree-chart-props';
import { getChild } from './pedigree-chart-utils';
import './pedigree-chart.scss';

// Leaving Pedigree-Chart-Admin and Pedigree-Chart as two seperate entities.
// We may want to show different features based on admin / other users later on.
/**
 * Displays the animal pedigree (3 levels deep).
 * @param props
 */
export const PedigreeChart: React.FC<PedigreeChartProps> = (props) => {
  const {
    animal,
    db,
    subIndex,
    asbvIds,
  } = props;
  const treeRef = useRef<any>(null);
  const treeWrapperRef = useRef<HTMLDivElement>(null);
  const [pedigreeDetails, setPedigreeDetails] = useState<Pedigree>();
  const [loading, setLoading] = useState<boolean>(true);
  const [treeTranslate, setTreeTranslate] = useState<{ x: number, y: number }>();
  const treeXOffset = 100; // Negative offset aligns the first node.
  const chipYOffset = -17.5; // Offset to center align the chip with the line.
  const treeMaxHeight: number = 3;

  // Recursively go through the pedigree tree & add Unknown dam / sires only until the treeMaxHeight
  function addUnknownChildToPedigree(_currentAnimal: Pedigree, currentHeight: number) {
    const currentAnimal: Pedigree = _currentAnimal;

    // Only add unknowns if we are < treeMaxHeight
    if (currentHeight < treeMaxHeight) {
      let hasDam: boolean = false;
      let hasSire: boolean = false;

      // Go through the children (If available) and check if they have a dam or sire
      currentAnimal.children.forEach((_child: Pedigree) => {
        const child: Pedigree = _child;
        if (child.type === PedigreeType.Dam) {
          hasDam = true;
          if (child.studId !== 'Unavailable') {
            addUnknownChildToPedigree(child, currentHeight + 1);
          }
        }
        if (child.type === PedigreeType.Sire) {
          hasSire = true;
          if (child.studId !== 'Unavailable') {
            addUnknownChildToPedigree(child, currentHeight + 1);
          }
        }
      });

      // There is no Dam, add an unknown one.
      if (!hasDam) {
        currentAnimal.children.push({
          animalId: 'Unavailable',
          type: PedigreeType.Dam,
          children: [],
          isLinked: false,
          studId: 'Unavailable',
          typeLabel: 'Dam',
        });
      }

      // There is no Sire, add an unknown one.
      if (!hasSire) {
        currentAnimal.children.push({
          animalId: 'Unavailable',
          type: PedigreeType.Sire,
          children: [],
          isLinked: false,
          studId: 'Unavailable',
          typeLabel: 'Sire',
        });
      }

      // Sort the children by type (Dam, Sire)
      currentAnimal.children.sort((a, b) => a.type - b.type);
    }

    // If we are back at the root child return the prodigee tree.
    if (currentHeight === 0) {
      // Ensure that the root animal HTML link tag is not disabled
      currentAnimal.isLinked = true;
      return currentAnimal;
    }
    return currentAnimal;
  }

  /**
   * Gets the pedigree details.
   */
  useEffect(() => {
    const getPedigree = async () => {
      try {
        const pedigree: Pedigree = await animalApi.getPedigree(animal.animalId, db, subIndex);
        // Add unknown dam and sires to the pedigree tree.
        setPedigreeDetails(pedigree);
      } finally {
        setLoading(false);
      }
    };

    getPedigree();
  }, [animal.animalId, db, subIndex]);

  // Adding unknowns to Pedigree tree is view related so can be performed each refresh.
  if (pedigreeDetails) {
    addUnknownChildToPedigree(pedigreeDetails, 0);
  }

  /**
   * Updates the tree offset based on the actual svg height.
   *
   * The container needs an explicit height as it won't stretch to
   * the height of the svg. Doing this we can set the height to be the
   * actual height of the svg and not waste whitespace.
   */
  useEffect(() => {
    const container = treeWrapperRef.current;
    const tree = treeRef.current;
    if (!loading && pedigreeDetails && container && tree) {
      const g = container.getElementsByTagName('g')[0];
      const boundingClientRect = g.getBoundingClientRect();

      const { height, width } = boundingClientRect;

      // Set the container height/width based on the svg values.
      // Include the chip offset in the height for alignments with trees where a single child exists
      // Include tree offset in width to reduce the width accounting for the offset.
      container.setAttribute('style', `height: ${height - chipYOffset}px; width: ${width + (treeXOffset)}px`);

      // Base the offset from the highest positioned childs offset.
      const lastChild = getChild(tree.state.data[0]);

      setTreeTranslate({
        x: treeXOffset,
        y: Math.abs(lastChild.x + chipYOffset),
      });
    }
  }, [chipYOffset, loading, pedigreeDetails, treeXOffset]);

  return (
    <Container className="sg-pedigree-chart">
      <div>
        <h2>Pedigree</h2>
      </div>
      <LoadContainer
        loading={loading}
      >
        {pedigreeDetails && (
          <ScrollShadow
            colour="white"
          >
            <div
              className="tree-container"
            >
              <div
                className="tree-container__wrapper"
                ref={treeWrapperRef}
              >
                <Tree
                  ref={treeRef}
                  data={pedigreeDetails}
                  translate={treeTranslate}
                  transitionDuration={0}
                  pathFunc="step"
                  collapsible={false}
                  zoomable={false}
                  allowForeignObjects
                  nodeSvgShape={{
                    shape: 'none',
                  }}
                  nodeSize={{
                    x: 314,
                    y: 140,
                  }}
                  nodeLabelComponent={{
                    render: <PedigreeChartNodeLabel
                      db={db}
                      subIndex={subIndex}
                      asbvIds={asbvIds}
                    />,
                    foreignObjectWrapper: {
                      y: chipYOffset,
                      x: -142.5,
                      height: 36,
                      width: 285,
                      cursor: 'auto',
                    },
                  }}
                  separation={{
                    siblings: 0.5,
                    nonSiblings: 1,
                  }}
                />
              </div>
            </div>
          </ScrollShadow>
        )}
      </LoadContainer>
    </Container>
  );
};

(PedigreeChart as any).whyDidYouRender = true;
